import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ConsultationVitalsSection,
  emptyVitalsFormState,
  formStateToVitalsInput,
  vitalsToFormState,
} from "@/components/clinical/consultation-vitals-form";
import {
  emptyPosologyFormState,
  posologyToInput,
  PrescriptionLinePosologyCell,
  PrescriptionPosologyFields,
  type PosologyFormState,
} from "@/components/clinical/prescription-posology-form";
import { PageHeader } from "@/components/PageHeader";
import { usePageMeta } from "@/context/PageMetaContext";
import {
  addPrescriptionLine,
  getConsultationById,
  getPrescriptionByConsultation,
  isPharmacistDirectPrescription,
  markPrescriptionSent,
  pharmacyOrderPath,
  removePrescriptionLine,
  setConsultationStatus,
  updateConsultation,
  updatePrescriptionLine,
} from "@/core/clinical/clinical-store";
import {
  CONSULTATION_STATUS_LABELS,
  formatPrescriptionPosology,
  formatVitalsSummary,
  PRESCRIPTION_SOURCE_LABELS,
  PRESCRIPTION_STATUS_LABELS,
} from "@/core/clinical/types";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  createImagingExam,
  ImagingExamValidationError,
  listImagingExamsByConsultation,
} from "@/core/imaging/imaging-store";
import {
  COMMON_IMAGING_TYPES,
  IMAGING_STATUS_LABELS,
} from "@/core/imaging/types";
import {
  createLabOrder,
  LabOrderValidationError,
  listLabOrdersByConsultation,
} from "@/core/laboratory/lab-store";
import {
  COMMON_LAB_TESTS,
  LAB_STATUS_LABELS,
} from "@/core/laboratory/types";
import {
  loadAppointmentPractitioners,
  listAppointmentPractitionersSync,
} from "@/core/practitioners/practitioner-store";
import type { AppointmentPractitioner } from "@/core/practitioners/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { listRecentNursingVitalsForPatient } from "@/core/nursing/vital-sign-store";
import { formatVitalSignRecordSummary } from "@/core/nursing/types";
import type { VitalSignRecord } from "@/core/nursing/types";
import {
  canSubmitPrescriptionToHub,
  ensureCoverageCaseForPrescription,
  getCoverageCaseForPrescription,
  submitPrescriptionToInsuranceHub,
} from "@/core/insurance/coverage-case-flow";
import { coverageCasePath } from "@/core/insurance/coverage-case-store";
import { getInsurerById } from "@/core/insurance/insurer-store";
import { COVERAGE_CASE_STATUS_LABELS } from "@/core/insurance/types";
import { notifyPrescriptionSentToPharmacy } from "@/core/notifications/pharmacy-notify";
import type { StockProduct } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

type ConsultationTab = "report" | "lab" | "imaging" | "prescription" | "insurance";

function parseConsultationTab(value: string | null): ConsultationTab {
  if (
    value === "lab" ||
    value === "imaging" ||
    value === "prescription" ||
    value === "insurance" ||
    value === "report"
  ) {
    return value;
  }
  return "report";
}

export function ConsultationDetailPage() {
  const { consultationId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseConsultationTab(searchParams.get("tab"));
  const setActiveTab = useCallback(
    (tab: ConsultationTab) => {
      if (tab === "report") {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab }, { replace: true });
      }
    },
    [setSearchParams],
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [motif, setMotif] = useState("");
  const [diagnostic, setDiagnostic] = useState("");
  const [acts, setActs] = useState("");
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [vitalsForm, setVitalsForm] = useState(emptyVitalsFormState);
  const [selectedNursingVitalId, setSelectedNursingVitalId] = useState("");

  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [lineQty, setLineQty] = useState("1");
  const [posologyForm, setPosologyForm] = useState<PosologyFormState>(() => emptyPosologyFormState());

  const [labTestName, setLabTestName] = useState(COMMON_LAB_TESTS[0]);
  const [labPrescriberId, setLabPrescriberId] = useState("");
  const [labNotes, setLabNotes] = useState("");
  const [labError, setLabError] = useState<string | null>(null);
  const [labAlert, setLabAlert] = useState<string | null>(null);
  const [imgAlert, setImgAlert] = useState<string | null>(null);
  const [imgExamType, setImgExamType] = useState<string>(COMMON_IMAGING_TYPES[0]);
  const [imgBodyPart, setImgBodyPart] = useState("");
  const [imgPrescriberId, setImgPrescriberId] = useState("");
  const [imgIndication, setImgIndication] = useState("");
  const [imgError, setImgError] = useState<string | null>(null);
  const [practitioners, setPractitioners] = useState<AppointmentPractitioner[]>(() =>
    listAppointmentPractitionersSync(),
  );

  const consultation = useMemo(() => {
    void refreshKey;
    return getConsultationById(consultationId);
  }, [consultationId, refreshKey]);

  const prescription = useMemo(() => {
    void refreshKey;
    if (!consultation) return null;
    return getPrescriptionByConsultation(consultation.id);
  }, [consultation, refreshKey]);

  const patient = useMemo(
    () => (consultation ? getPatientById(consultation.patientId) : null),
    [consultation],
  );

  usePageMeta(
    patient && consultation
      ? {
          pageTitle: `${
            prescription && isPharmacistDirectPrescription(prescription)
              ? "Ordonnance officine"
              : "Consultation"
          } — ${patientFullName(patient)}`,
          breadcrumbLabel: patientFullName(patient),
        }
      : null,
  );

  const labOrders = useMemo(() => {
    void refreshKey;
    if (!consultation) return [];
    return listLabOrdersByConsultation(consultation.id);
  }, [consultation, refreshKey]);

  const imagingExams = useMemo(() => {
    void refreshKey;
    if (!consultation) return [];
    return listImagingExamsByConsultation(consultation.id);
  }, [consultation, refreshKey]);

  const nursingVitalsOptions = useMemo(() => {
    if (!consultation) return [];
    void refreshKey;
    return listRecentNursingVitalsForPatient(consultation.patientId);
  }, [consultation, refreshKey]);

  const latestNursingVitals = useMemo((): VitalSignRecord | null => {
    if (!nursingVitalsOptions.length) return null;
    if (selectedNursingVitalId) {
      return nursingVitalsOptions.find((r) => r.id === selectedNursingVitalId) ?? nursingVitalsOptions[0]!;
    }
    return nursingVitalsOptions[0]!;
  }, [nursingVitalsOptions, selectedNursingVitalId]);

  useEffect(() => {
    const refreshNursing = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:nursing", refreshNursing);
    return () => window.removeEventListener("hospitol:nursing", refreshNursing);
  }, []);

  const handleImportNursingVitals = useCallback(() => {
    if (!latestNursingVitals) return;
    setVitalsForm(vitalsToFormState(latestNursingVitals.vitals));
    setSavedMsg("Constantes importées depuis le relevé infirmier.");
  }, [latestNursingVitals]);

  useEffect(() => {
    let active = true;
    loadAppointmentPractitioners().then((list) => {
      if (active) {
        setPractitioners(list);
        setLabPrescriberId((prev) => prev || list[0]?.id || "");
        setImgPrescriberId((prev) => prev || list[0]?.id || "");
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!consultation) return;
    setMotif(consultation.motif);
    setDiagnostic(consultation.diagnostic);
    setActs(consultation.acts);
    setVitalsForm(vitalsToFormState(consultation.vitals));
  }, [consultation]);

  useEffect(() => {
    void (async () => {
      setLoadingProducts(true);
      setProductsError(null);
      try {
        const list = await pharmacyInventoryApi.listPharmacyProducts();
        setProducts(list.filter((p) => p.status === "ACTIVE"));
        if (list.length === 0) {
          setProductsError(
            "Aucun médicament chargé — vérifiez le seed ISC et reconnectez-vous (droits catalogue).",
          );
        }
      } catch (err) {
        setProducts([]);
        setProductsError(
          err instanceof IscApiError
            ? err.status === 403
              ? "Accès catalogue refusé — votre compte doit avoir le rôle clinique (hospitol.clinical.read)."
              : err.message
            : err instanceof Error
              ? err.message
              : "Catalogue inaccessible.",
        );
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleSaveConsultation = (e: FormEvent) => {
    e.preventDefault();
    if (!consultation) return;
    updateConsultation(consultation.id, {
      motif,
      diagnostic,
      acts,
      vitals: formStateToVitalsInput(vitalsForm, consultation.vitals),
    });
    setSavedMsg("Consultation enregistrée.");
    refresh();
    window.setTimeout(() => setSavedMsg(null), 2500);
  };

  const handleClose = () => {
    if (!consultation) return;
    setConsultationStatus(consultation.id, "closed");
    refresh();
  };

  const handleReopen = () => {
    if (!consultation) return;
    setConsultationStatus(consultation.id, "open");
    refresh();
  };

  const handleAddLine = (e: FormEvent) => {
    e.preventDefault();
    if (!prescription) return;
    const product = products.find((p) => p.id === selectedProductId);
    if (!product) return;
    addPrescriptionLine(prescription.id, {
      productId: product.id,
      productLabel: product.label,
      quantity: Number(lineQty) || 1,
      ...posologyToInput(posologyForm),
    });
    setSelectedProductId("");
    setLineQty("1");
    setPosologyForm(emptyPosologyFormState());
    refresh();
  };

  const handleLabRequest = (e: FormEvent) => {
    e.preventDefault();
    if (!consultation || !labPrescriberId) {
      setLabError("Prescripteur requis.");
      return;
    }
    try {
      const result = createLabOrder({
        patientId: consultation.patientId,
        prescriberId: labPrescriberId,
        consultationId: consultation.id,
        testName: labTestName,
        clinicalNotes: labNotes || diagnostic || motif,
      });
      setLabNotes("");
      setLabError(null);
      if (result.insurerPending) {
        setLabAlert(
          result.alertMessage ??
            "Demande enregistrée — validation assureur en cours. Le laboratoire peut poursuivre le prélèvement.",
        );
        setSavedMsg(null);
      } else {
        setLabAlert(null);
        setSavedMsg("Demande laboratoire enregistrée.");
        window.setTimeout(() => setSavedMsg(null), 2500);
      }
      refresh();
    } catch (err) {
      setLabError(
        err instanceof LabOrderValidationError ? err.message : "Impossible de créer la demande.",
      );
    }
  };

  const handleImagingRequest = (e: FormEvent) => {
    e.preventDefault();
    if (!consultation || !imgPrescriberId) {
      setImgError("Prescripteur requis.");
      return;
    }
    try {
      const result = createImagingExam({
        patientId: consultation.patientId,
        prescriberId: imgPrescriberId,
        consultationId: consultation.id,
        examType: imgExamType,
        bodyPart: imgBodyPart,
        clinicalIndication: imgIndication || diagnostic || motif,
      });
      setImgBodyPart("");
      setImgIndication("");
      setImgError(null);
      if (result.insurerPending) {
        setImgAlert(
          result.alertMessage ??
            "Demande enregistrée — validation assureur en cours. L'imagerie peut planifier l'examen.",
        );
        setSavedMsg(null);
      } else {
        setImgAlert(null);
        setSavedMsg("Demande imagerie enregistrée.");
        window.setTimeout(() => setSavedMsg(null), 2500);
      }
      refresh();
    } catch (err) {
      setImgError(
        err instanceof ImagingExamValidationError ? err.message : "Impossible de créer la demande.",
      );
    }
  };

  const handleSendToPharmacy = useCallback(() => {
    if (!prescription || !patient) return;
    if (prescription.lines.length === 0) {
      setSavedMsg("Ajoutez au moins un médicament avant envoi.");
      return;
    }
    const isDirect = isPharmacistDirectPrescription(prescription);

    if (prescription.status === "draft") {
      markPrescriptionSent(prescription.id);
    }

    if (isDirect) {
      setSavedMsg("Ordonnance officine — poursuivre la validation stock.");
      navigate(pharmacyOrderPath(prescription.id));
      return;
    }

    void notifyPrescriptionSentToPharmacy({
      prescriptionId: prescription.id,
      patientName: patientFullName(patient),
    });
    ensureCoverageCaseForPrescription(prescription.id);
    setSavedMsg("Ordonnance envoyée à la pharmacie — le pharmacien sera notifié.");
    refresh();
    window.setTimeout(() => setSavedMsg(null), 2500);
  }, [navigate, patient, prescription]);

  const handleSubmitToInsuranceHub = useCallback(() => {
    if (!prescription) return;
    void submitPrescriptionToInsuranceHub(prescription.id).then((result) => {
      if (!result) {
        setSavedMsg("Impossible de soumettre — vérifiez le partenariat hub et les règles assureur.");
        return;
      }
      if (result.via === "api") {
        setSavedMsg("Dossier envoyé au hub assureur (API).");
      } else if (result.error) {
        setSavedMsg(`${result.error} Lien d'import ouvert en secours.`);
        window.open(result.importUrl, "_blank", "noopener,noreferrer");
      } else {
        window.open(result.importUrl, "_blank", "noopener,noreferrer");
        setSavedMsg("Dossier soumis au hub assureur.");
      }
      refresh();
      window.setTimeout(() => setSavedMsg(null), 3000);
    });
  }, [prescription]);

  if (!consultation || !patient) {
    return (
      <div className="alert alert-warning">
        Consultation introuvable.{" "}
        <Link to={routes.consultations}>Retour à la liste</Link>
      </div>
    );
  }

  const canEdit = consultation.status === "open";
  const canSend =
    prescription &&
    prescription.lines.length > 0 &&
    prescription.status !== "dispensed";
  const isDirectRx = prescription ? isPharmacistDirectPrescription(prescription) : false;
  const insurer = patient.insurerId ? getInsurerById(patient.insurerId) : null;
  const insuranceHub = prescription
    ? canSubmitPrescriptionToHub(prescription.id)
    : null;
  const sendActionLabel = isDirectRx
    ? "Continuer — validation stock"
    : "Envoyer à la pharmacie";
  const sendActionHint = isDirectRx
    ? "Passe à la validation pharmacien (officine directe)."
    : "Envoie l'ordonnance à la file pharmacie — sans facturation.";

  const prescriptionCoverageCase = prescription
    ? getCoverageCaseForPrescription(prescription.id)
    : null;
  const labCoverageCount = labOrders.filter((o) => o.coverageCaseId).length;
  const hasInsuranceContext =
    !!insurer ||
    !!prescriptionCoverageCase ||
    labCoverageCount > 0 ||
    !!insuranceHub?.validationRequired;

  const visibleTabs: Array<{ id: ConsultationTab; label: string; count?: number }> = [
    { id: "report", label: "Compte-rendu" },
    ...(!isDirectRx
      ? [
          { id: "lab" as const, label: "Laboratoire", count: labOrders.length },
          { id: "imaging" as const, label: "Imagerie", count: imagingExams.length },
        ]
      : []),
    { id: "prescription", label: "Ordonnance", count: prescription?.lines.length ?? 0 },
    ...(hasInsuranceContext
      ? [
          {
            id: "insurance" as const,
            label: "Assurance",
            count: (prescriptionCoverageCase ? 1 : 0) + labCoverageCount,
          },
        ]
      : []),
  ];

  const effectiveTab = visibleTabs.some((tab) => tab.id === activeTab) ? activeTab : "report";

  const patientPanel = (
    <div className="ph-invoice-sheet h-100">
      <div className="ph-invoice-section-title">Patient</div>
      <dl className="row mb-0 small">
        <dt className="col-sm-4 text-muted">Nom</dt>
        <dd className="col-sm-8 fw-medium">{patientFullName(patient)}</dd>
        <dt className="col-sm-4 text-muted">Téléphone</dt>
        <dd className="col-sm-8">{patient.phone || "—"}</dd>
        <dt className="col-sm-4 text-muted">Adresse</dt>
        <dd className="col-sm-8">{patient.address || "—"}</dd>
        {insurer && (
          <>
            <dt className="col-sm-4 text-muted">Assureur</dt>
            <dd className="col-sm-8">{insurer.name}</dd>
          </>
        )}
      </dl>
      {prescription && (
        <p className="small text-muted mt-3 mb-0">
          Ordonnance :{" "}
          <span className={`hosp-rx-badge hosp-rx-badge--${prescription.status}`}>
            {PRESCRIPTION_STATUS_LABELS[prescription.status]}
          </span>
        </p>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        title={`${isDirectRx ? "Ordonnance officine" : "Consultation"} — ${patientFullName(patient)}`}
        description={`Dossier ${patient.fileNumber} · ${new Date(consultation.createdAt).toLocaleString("fr-FR")}${isDirectRx ? ` · ${PRESCRIPTION_SOURCE_LABELS.pharmacy_direct}` : ""}`}
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.consultations} className="btn btn-outline-secondary btn-sm">
              Liste
            </Link>
            {canEdit ? (
              <button type="button" className="btn btn-outline-dark btn-sm" onClick={handleClose}>
                Clôturer
              </button>
            ) : (
              <button type="button" className="btn btn-outline-dark btn-sm" onClick={handleReopen}>
                Rouvrir
              </button>
            )}
            {canSend && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSendToPharmacy}
              >
                {sendActionLabel}
              </button>
            )}
          </div>
        }
      />

      {savedMsg && <div className="alert alert-success py-2">{savedMsg}</div>}

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-8">
          <nav className="ph-detail-tabs mb-3" role="tablist" aria-label="Sections consultation">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={effectiveTab === tab.id}
                className={`ph-detail-tab ${effectiveTab === tab.id ? "ph-detail-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="ph-detail-tab-badge">{tab.count}</span>
                )}
              </button>
            ))}
          </nav>

          {effectiveTab === "report" && (
            <form className="d-flex flex-column gap-3" onSubmit={handleSaveConsultation}>
              <div className="ph-invoice-sheet">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="ph-invoice-section-title mb-0">Compte-rendu</div>
                  <span
                    className={`hosp-clinical-badge hosp-clinical-badge--${consultation.status}`}
                  >
                    {CONSULTATION_STATUS_LABELS[consultation.status]}
                  </span>
                </div>

                <label className="form-label small fw-semibold" htmlFor="con-motif-edit">
                  Motif
                </label>
                <input
                  id="con-motif-edit"
                  className="form-control form-control-sm mb-3"
                  value={motif}
                  disabled={!canEdit}
                  onChange={(e) => setMotif(e.target.value)}
                />

                <label className="form-label small fw-semibold" htmlFor="con-diagnostic">
                  Diagnostic (texte libre)
                </label>
                <textarea
                  id="con-diagnostic"
                  className="form-control form-control-sm mb-3"
                  rows={3}
                  placeholder="Hypothèse diagnostique, observations…"
                  value={diagnostic}
                  disabled={!canEdit}
                  onChange={(e) => setDiagnostic(e.target.value)}
                />

                <label className="form-label small fw-semibold" htmlFor="con-acts">
                  Actes réalisés
                </label>
                <textarea
                  id="con-acts"
                  className="form-control form-control-sm"
                  rows={2}
                  placeholder="Ex. consultation, pansement, injection…"
                  value={acts}
                  disabled={!canEdit}
                  onChange={(e) => setActs(e.target.value)}
                />
              </div>

              <div className="ph-invoice-sheet">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <div className="ph-invoice-section-title mb-0">Constantes vitales</div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {consultation.vitals && (
                      <span className="small text-muted">
                        {formatVitalsSummary(consultation.vitals)}
                      </span>
                    )}
                  </div>
                </div>

                {canEdit && (
                  <div className="mb-3 p-2 bg-light rounded border">
                    <div className="small fw-semibold mb-2">Relevé infirmier</div>
                    {nursingVitalsOptions.length === 0 ? (
                      <p className="small text-muted mb-0">
                        Aucun relevé récent — l&apos;infirmier(ère) peut saisir les constantes dans{" "}
                        <Link to={routes.nursingCare}>Soins infirmiers</Link>.
                      </p>
                    ) : (
                      <>
                        {nursingVitalsOptions.length > 1 && (
                          <select
                            className="form-select form-select-sm mb-2"
                            style={{ maxWidth: "20rem" }}
                            value={selectedNursingVitalId || nursingVitalsOptions[0]!.id}
                            onChange={(e) => setSelectedNursingVitalId(e.target.value)}
                          >
                            {nursingVitalsOptions.map((record) => (
                              <option key={record.id} value={record.id}>
                                {new Date(record.recordedAt).toLocaleString("fr-FR")} —{" "}
                                {formatVitalSignRecordSummary(record).slice(0, 48)}
                              </option>
                            ))}
                          </select>
                        )}
                        {latestNursingVitals && (
                          <p className="small text-muted mb-2">
                            {formatVitalSignRecordSummary(latestNursingVitals)}
                          </p>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleImportNursingVitals}
                        >
                          Importer les constantes infirmières
                        </button>
                      </>
                    )}
                  </div>
                )}

                <ConsultationVitalsSection
                  form={vitalsForm}
                  vitals={consultation.vitals}
                  canEdit={canEdit}
                  onChange={(patch) => setVitalsForm((prev) => ({ ...prev, ...patch }))}
                />
              </div>

              {canEdit && (
                <button type="submit" className="btn btn-primary btn-sm align-self-start">
                  Enregistrer
                </button>
              )}
            </form>
          )}

          {effectiveTab === "lab" && (
            <div className="ph-invoice-sheet">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="ph-invoice-section-title mb-0">Laboratoire</div>
                <Link to={routes.laboratory} className="btn btn-outline-secondary btn-sm">
                  Module labo
                </Link>
              </div>

              {labOrders.length > 0 && (
                <div className="table-responsive ph-diag-table mb-3">
                  <table className="table table-sm ph-dash-table mb-0">
                    <thead>
                      <tr>
                        <th>N°</th>
                        <th>Analyse</th>
                        <th>Statut</th>
                        <th>Assurance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labOrders.map((order) => (
                        <tr key={order.id} className="ph-diag-row">
                          <td data-label="N°">
                            <span className="ph-inv-badge">{order.orderNumber}</span>
                          </td>
                          <td data-label="Analyse" className="small">
                            {order.testName}
                          </td>
                          <td data-label="Statut">
                            <span className={`hosp-diag-badge hosp-diag-badge--${order.status}`}>
                              {LAB_STATUS_LABELS[order.status]}
                            </span>
                          </td>
                          <td data-label="Assurance" className="small">
                            {order.coverageCaseId ? (
                              <Link to={coverageCasePath(order.coverageCaseId)}>Dossier assureur</Link>
                            ) : order.insurerApproval === "approved" ? (
                              <span className="text-success">Validé</span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {canEdit && !isDirectRx && (
                <form className="row g-2 align-items-end" onSubmit={handleLabRequest}>
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-lab-test">
                      Analyse
                    </label>
                    <select
                      id="con-lab-test"
                      className="form-select form-select-sm"
                      value={labTestName}
                      onChange={(e) => setLabTestName(e.target.value as typeof labTestName)}
                    >
                      {COMMON_LAB_TESTS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-lab-doc">
                      Prescripteur
                    </label>
                    <select
                      id="con-lab-doc"
                      className="form-select form-select-sm"
                      value={labPrescriberId}
                      onChange={(e) => setLabPrescriberId(e.target.value)}
                      required
                    >
                      <option value="">— Médecin —</option>
                      {practitioners.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-lab-notes">
                      Indication
                    </label>
                    <input
                      id="con-lab-notes"
                      className="form-control form-control-sm"
                      placeholder="Optionnel"
                      value={labNotes}
                      onChange={(e) => setLabNotes(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-1">
                    <button type="submit" className="btn btn-sm btn-outline-primary w-100">
                      Demander
                    </button>
                  </div>
                </form>
              )}
              {labError && <p className="small text-danger mb-0 mt-2">{labError}</p>}
              {labAlert && (
                <div className="alert alert-warning py-2 small mt-2 mb-0">
                  {labAlert}{" "}
                  <Link to={routes.insuranceCases}>Voir les dossiers assurance</Link>
                </div>
              )}
              {!canEdit && labOrders.length === 0 && (
                <p className="text-muted small mb-0">Aucune demande laboratoire liée.</p>
              )}
            </div>
          )}

          {effectiveTab === "imaging" && (
            <div className="ph-invoice-sheet">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="ph-invoice-section-title mb-0">Imagerie</div>
                <Link to={routes.imaging} className="btn btn-outline-secondary btn-sm">
                  Module imagerie
                </Link>
              </div>

              {imagingExams.length > 0 && (
                <div className="table-responsive ph-diag-table mb-3">
                  <table className="table table-sm ph-dash-table mb-0">
                    <thead>
                      <tr>
                        <th>N°</th>
                        <th>Examen</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {imagingExams.map((exam) => (
                        <tr key={exam.id} className="ph-diag-row">
                          <td data-label="N°">
                            <span className="ph-inv-badge">{exam.examNumber}</span>
                          </td>
                          <td data-label="Examen" className="small">
                            {exam.examType}
                            {exam.bodyPart ? ` — ${exam.bodyPart}` : ""}
                          </td>
                          <td data-label="Statut">
                            <span className={`hosp-diag-badge hosp-diag-badge--${exam.status}`}>
                              {IMAGING_STATUS_LABELS[exam.status]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {canEdit && !isDirectRx && (
                <form className="row g-2 align-items-end" onSubmit={handleImagingRequest}>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-img-type">
                      Examen
                    </label>
                    <select
                      id="con-img-type"
                      className="form-select form-select-sm"
                      value={imgExamType}
                      onChange={(e) => setImgExamType(e.target.value)}
                    >
                      {COMMON_IMAGING_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6 col-md-2">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-img-part">
                      Région
                    </label>
                    <input
                      id="con-img-part"
                      className="form-control form-control-sm"
                      placeholder="Ex. Thorax"
                      value={imgBodyPart}
                      onChange={(e) => setImgBodyPart(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-img-doc">
                      Prescripteur
                    </label>
                    <select
                      id="con-img-doc"
                      className="form-select form-select-sm"
                      value={imgPrescriberId}
                      onChange={(e) => setImgPrescriberId(e.target.value)}
                      required
                    >
                      <option value="">— Médecin —</option>
                      {practitioners.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label small fw-semibold mb-1" htmlFor="con-img-indication">
                      Indication
                    </label>
                    <input
                      id="con-img-indication"
                      className="form-control form-control-sm"
                      placeholder="Optionnel"
                      value={imgIndication}
                      onChange={(e) => setImgIndication(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-1">
                    <button type="submit" className="btn btn-sm btn-outline-primary w-100">
                      Demander
                    </button>
                  </div>
                </form>
              )}
              {imgError && <p className="small text-danger mb-0 mt-2">{imgError}</p>}
              {imgAlert && (
                <div className="alert alert-warning py-2 small mt-2 mb-0">
                  {imgAlert}{" "}
                  <Link to={routes.insuranceCases}>Voir les dossiers assurance</Link>
                </div>
              )}
              {!canEdit && imagingExams.length === 0 && (
                <p className="text-muted small mb-0">Aucune demande imagerie liée.</p>
              )}
            </div>
          )}

          {effectiveTab === "prescription" && (
            <div className="ph-invoice-sheet">
              <div className="ph-invoice-section-title">Ordonnance</div>

              {canEdit && prescription && (
                <form className="mb-3" onSubmit={handleAddLine}>
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-5">
                      <label className="form-label small fw-semibold mb-1" htmlFor="rx-product">
                        Médicament (stock)
                      </label>
                      <select
                        id="rx-product"
                        className="form-select form-select-sm"
                        value={selectedProductId}
                        disabled={loadingProducts}
                        onChange={(e) => setSelectedProductId(e.target.value)}
                      >
                        <option value="">
                          {loadingProducts
                            ? "Chargement…"
                            : products.length === 0
                              ? "— Aucun médicament —"
                              : "— Choisir un article —"}
                        </option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                      {productsError && (
                        <p className="small text-warning mb-0 mt-1">{productsError}</p>
                      )}
                    </div>
                    <div className="col-12 col-sm-6 col-md-2">
                      <label className="form-label small fw-semibold mb-1" htmlFor="rx-qty">
                        Qté
                      </label>
                      <input
                        id="rx-qty"
                        type="number"
                        min="1"
                        className="form-control form-control-sm"
                        value={lineQty}
                        onChange={(e) => setLineQty(e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-md-2">
                      <button
                        type="submit"
                        className="btn btn-sm btn-outline-primary w-100"
                        disabled={!selectedProductId}
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                  <PrescriptionPosologyFields
                    form={posologyForm}
                    onChange={(patch) => setPosologyForm((prev) => ({ ...prev, ...patch }))}
                  />
                </form>
              )}

              {!prescription || prescription.lines.length === 0 ? (
                <p className="text-muted small mb-0">
                  Aucune ligne — ajoutez des médicaments depuis le catalogue stock.
                </p>
              ) : (
                <div className="table-responsive ph-diag-table">
                  <table className="table table-sm ph-dash-table mb-0">
                    <thead>
                      <tr>
                        <th>Médicament</th>
                        <th>Qté</th>
                        <th>Posologie</th>
                        {canEdit && <th className="text-end">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {prescription.lines.map((line) => (
                        <tr key={line.id} className="ph-diag-row">
                          <td data-label="Médicament" className="fw-medium">
                            {line.productLabel}
                          </td>
                          <td data-label="Qté">
                            {canEdit ? (
                              <input
                                type="number"
                                min="1"
                                className="form-control form-control-sm"
                                style={{ width: "4rem" }}
                                value={line.quantity}
                                onChange={(e) => {
                                  updatePrescriptionLine(prescription.id, line.id, {
                                    quantity: Number(e.target.value) || 1,
                                  });
                                  refresh();
                                }}
                              />
                            ) : (
                              line.quantity
                            )}
                          </td>
                          <td data-label="Posologie" className="small">
                            {canEdit ? (
                              <PrescriptionLinePosologyCell
                                line={line}
                                canEdit
                                onUpdate={(patch) => {
                                  updatePrescriptionLine(prescription.id, line.id, patch);
                                  refresh();
                                }}
                              />
                            ) : (
                              formatPrescriptionPosology(line)
                            )}
                          </td>
                          {canEdit && (
                            <td data-label="Actions" className="text-end">
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0"
                                onClick={() => {
                                  removePrescriptionLine(prescription.id, line.id);
                                  refresh();
                                }}
                              >
                                Retirer
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {canSend && (
                <div className="mt-3 pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSendToPharmacy}
                  >
                    {sendActionLabel}
                  </button>
                  <p className="small text-muted mb-0 mt-1">{sendActionHint}</p>
                  {hasInsuranceContext && insuranceHub?.validationRequired && (
                    <p className="small text-warning mb-0 mt-2">
                      Prise en charge assureur à valider —{" "}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={() => setActiveTab("insurance")}
                      >
                        onglet Assurance
                      </button>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {effectiveTab === "insurance" && (
            <div className="ph-invoice-sheet">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="ph-invoice-section-title mb-0">Assurance médicale</div>
                <Link to={routes.insuranceCases} className="btn btn-outline-secondary btn-sm">
                  Tous les dossiers
                </Link>
              </div>

              {!insurer ? (
                <p className="text-muted small mb-0">
                  Patient sans assureur enregistré — renseignez la couverture sur la fiche patient.
                </p>
              ) : (
                <dl className="row small mb-4">
                  <dt className="col-sm-3 text-muted">Assureur</dt>
                  <dd className="col-sm-9 fw-medium">{insurer.name}</dd>
                  <dt className="col-sm-3 text-muted">N° adhérent</dt>
                  <dd className="col-sm-9">{patient.memberNumber || "—"}</dd>
                  <dt className="col-sm-3 text-muted">N° police</dt>
                  <dd className="col-sm-9">{patient.policyNumber || "—"}</dd>
                </dl>
              )}

              {prescription && prescription.lines.length > 0 && (
                <div className="mb-4">
                  <h4 className="h6 fw-semibold">Ordonnance courante</h4>
                  {prescriptionCoverageCase ? (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className={`hosp-ins-badge hosp-ins-badge--${prescriptionCoverageCase.status}`}>
                        {COVERAGE_CASE_STATUS_LABELS[prescriptionCoverageCase.status]}
                      </span>
                      <Link
                        to={coverageCasePath(prescriptionCoverageCase.id)}
                        className="small"
                      >
                        Dossier {prescriptionCoverageCase.label || prescriptionCoverageCase.id.slice(-8)}
                      </Link>
                    </div>
                  ) : insuranceHub?.validationRequired ? (
                    <div className="mt-2">
                      {insuranceHub.canSubmit ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleSubmitToInsuranceHub}
                          >
                            Soumettre au hub assureur
                          </button>
                          <p className="small text-muted mb-0 mt-2">
                            {insuranceHub.reasons.join(" · ")}
                          </p>
                        </>
                      ) : (
                        <p className="small text-warning mb-0">
                          {insuranceHub.reasons[0] ??
                            "Configurez le partenariat hub pour soumettre le dossier."}{" "}
                          <Link to={routes.insurancePartnerships}>Partenariats</Link>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="small text-muted mb-0">
                      Pas de validation assureur requise pour cette ordonnance.
                    </p>
                  )}
                </div>
              )}

              {labOrders.length > 0 && (
                <div>
                  <h4 className="h6 fw-semibold">Laboratoire (cette consultation)</h4>
                  <ul className="small mb-0 ps-3">
                    {labOrders.map((order) => (
                      <li key={order.id} className="mb-1">
                        {order.testName} — {LAB_STATUS_LABELS[order.status]}
                        {order.coverageCaseId ? (
                          <>
                            {" · "}
                            <Link to={coverageCasePath(order.coverageCaseId)}>Dossier assureur</Link>
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-lg-4">{patientPanel}</div>
      </div>
    </div>
  );
}
